import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Icon from './Icon';

// subs: ana menünün altında açılan sekmeler (?tab=...). İlk sekme varsayılan değil — default ayrı.
const PAGES = [
  { key: '/today', label: 'Bugünüm' },
  { key: '/dashboard', label: 'Dashboard' },
  { key: '/leads', label: 'Leads' },
  { key: '/accounts', label: 'Hesaplar' },
  { key: '/tasks', label: 'Görevler', countKey: 'suggestions', defaultSub: 'tasks', subs: [
    { key: 'tasks', label: 'Görevler' },
    { key: 'meeting', label: 'Toplantıdan Gelenler', countKey: 'suggestions' },
  ] },
  { key: '/meetings', label: 'Toplantılar', defaultSub: 'calendar', subs: [
    { key: 'calendar', label: 'Takvim' },
    { key: 'upcoming', label: 'Yaklaşan', countKey: 'upcoming' },
    { key: 'recorded', label: 'Kayıtlar', countKey: 'recorded' },
    { key: 'other', label: 'Diğer', countKey: 'other' },
  ] },
  { key: '/settings', label: 'Ayarlar' },
];

// Sayfalar alt sekme sayılarını buradan bildirir: useSubnavCounts({ upcoming: 3, ... })
const SubnavContext = createContext(() => {});
export function useSubnavCounts(counts) {
  const set = useContext(SubnavContext);
  const sig = JSON.stringify(counts);
  useEffect(() => { set(JSON.parse(sig)); }, [set, sig]);
}

// Sayfanın aktif alt sekmesi (URL ?tab=, yoksa menüdeki varsayılan)
export function useSubTab(pageKey) {
  const [params] = useSearchParams();
  const page = PAGES.find(p => p.key === pageKey);
  const t = params.get('tab');
  return page && page.subs && page.subs.some(s => s.key === t) ? t : page?.defaultSub;
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const menuRef = useRef(null);
  const navRef = useRef(null);
  const [params, setParams] = useSearchParams();

  // Görevler sekmesindeki rozet: bana atanmış, bekleyen toplantı maddeleri
  useEffect(() => {
    api.get('/meetings/action-items')
      .then(r => setCounts(c => ({ ...c, suggestions: r.data.length })))
      .catch(() => {});
  }, [location.pathname]);

  // Dar ekranda aktif menü sekmesi görünür alana kaysın
  useEffect(() => {
    const el = document.querySelector('.topnav__item--active');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  // /accounts/12 gibi alt sayfalarda da ilgili menü aktif görünsün
  const isActive = (key) => location.pathname === key || location.pathname.startsWith(key + '/');
  const activePage = PAGES.find(p => isActive(p.key));
  const activeSub = activePage?.subs
    ? (activePage.subs.some(s => s.key === params.get('tab')) ? params.get('tab') : activePage.defaultSub)
    : null;


  const pushCounts = React.useCallback((c) => setCounts(prev => ({ ...prev, ...c })), []);

  return (
    <div className="layout">
      <header className="topbar">
        <div className="topbar__row">
          <div className="topbar__left" />
          <div className="brand" onClick={() => navigate('/today')} title="Bugünüm">
            <span className="brand__word">BOLDRANKS</span>
            <span className="brand__sig">CRM</span>
          </div>

          <div className="topbar__right">
            <div className="usermenu" ref={menuRef}>
              <button type="button" className="usermenu__btn" onClick={() => setMenuOpen(o => !o)}>
                <span className="usermenu__avatar">{initials}</span>
                <span className="usermenu__text">
                  <div className="usermenu__name">{user?.name || 'Kullanıcı'}</div>
                  <div className="usermenu__role">{user?.role || 'viewer'}</div>
                </span>
                <Icon name="chevronDown" size={14} style={{ color: 'var(--text-muted)' }} />
              </button>
              {menuOpen && (
                <div className="usermenu__panel">
                  <button type="button" className="usermenu__item" onClick={() => { setMenuOpen(false); navigate('/settings'); }}>
                    <Icon name="settings" /> Ayarlar
                  </button>
                  <button type="button" className="usermenu__item" onClick={handleLogout}>
                    <Icon name="logout" /> Çıkış yap
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <nav className="topnav" ref={navRef}>
          {PAGES.map(p => (
            <button
              key={p.key}
              type="button"
              className={`topnav__item ${isActive(p.key) ? 'topnav__item--active' : ''}`}
              onClick={() => navigate(p.key)}
            >
              {p.label}
              {p.countKey && counts[p.countKey] > 0 && (
                <span className="topnav__count" title="Sana atanmış, bekleyen toplantı maddeleri">{counts[p.countKey]}</span>
              )}
            </button>
          ))}
        </nav>

        {activePage?.subs && (
          <nav className="subnav" key={activePage.key}>
            <div className="subnav__inner">
            {activePage.subs.map(sub => (
              <button
                key={sub.key}
                type="button"
                className={`subnav__item ${activeSub === sub.key ? 'subnav__item--active' : ''}`}
                onClick={() => setParams(sub.key === activePage.defaultSub ? {} : { tab: sub.key })}
              >
                {sub.label}
                {sub.countKey && counts[sub.countKey] != null && (
                  <span className="subnav__count">{counts[sub.countKey]}</span>
                )}
              </button>
            ))}
            </div>
          </nav>
        )}
      </header>

      <main className="main-content">
        <SubnavContext.Provider value={pushCounts}>
          <div className="page-wrap">{children}</div>
        </SubnavContext.Provider>
      </main>
    </div>
  );
}
